/**
 * Public Settings API Hook
 *
 * Fetches school settings (swim levels, locations, program types) without authentication.
 * Replaces useSchoolSettings for unauthenticated pages like /browse-classes.
 */

import { useQuery } from "@tanstack/react-query"

import type { PublicSetting } from "../types"

const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL
  if (!envUrl) return "/api/v1"
  if (envUrl.endsWith("/api/v1")) return envUrl
  return `${envUrl.replace(/\/$/, "")}/api/v1`
}

const API_BASE_URL = getApiBaseUrl()

interface PublicSettingListResponse {
  items: PublicSetting[]
  total: number
}

export function usePublicSettings() {
  const { data, ...rest } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async (): Promise<PublicSettingListResponse> => {
      const response = await fetch(`${API_BASE_URL}/public/settings`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch settings: ${response.statusText}`)
      }

      const json = await response.json()
      return (json.data ?? json) as PublicSettingListResponse
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const items = data?.items ?? []

  return {
    ...rest,
    swimLevels: items.filter((s) => s.category === "swim_level"),
    locations: items.filter((s) => s.category === "location"),
    programTypes: items.filter((s) => s.category === "program_type"),
  }
}
